import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Banknote,
  FileText,
  Receipt,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed, num } from "@/lib/format";
import { printCashReport } from "@/lib/cashReportPrint";
import { prepareBillPrintWindow } from "@/lib/billPrint";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";
import PaymentMethodFilter from "@/components/reports/PaymentMethodFilter";
import OrderTypeFilter from "@/components/reports/OrderTypeFilter";
import { DayEndConfirmDialog } from "@/components/pos/DayEndConfirmDialog";
import { formatReportFilterLine } from "@/lib/reportFilters";

const TABS = [
  { id: "collection", label: "Cash Collection", path: "/reports/cash-collection", icon: Banknote },
  { id: "x-report", label: "X-Report", path: "/reports/x-report", icon: Receipt },
  { id: "z-report", label: "Z-Report", path: "/reports/z-report", icon: Receipt },
  { id: "day-book", label: "Sales Day Book", path: "/reports/sales-day-book", icon: FileText },
];

const ENDPOINTS = {
  collection: "/reports/cash/collection",
  "x-report": "/reports/cash/x-report",
  "z-report": "/reports/cash/z-report",
  "day-book": "/reports/cash/day-book",
};

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function CashReports() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const cashierName = user?.name || user?.username || "ADMIN";

  const activeTab = useMemo(() => {
    if (location.pathname.includes("x-report")) return "x-report";
    if (location.pathname.includes("z-report")) return "z-report";
    if (location.pathname.includes("sales-day-book")) return "day-book";
    return "collection";
  }, [location.pathname]);

  const isRange = activeTab === "collection";
  const needsCounter = activeTab === "x-report" || activeTab === "z-report" || activeTab === "day-book";
  const isLiveSessionReport = activeTab === "x-report";

  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [singleDate, setSingleDate] = useState(today);
  const [counterId, setCounterId] = useState("all");
  const [orderType, setOrderType] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [denomLines, setDenomLines] = useState([]);
  const [dayEnding, setDayEnding] = useState(false);
  const [dayEndConfirmOpen, setDayEndConfirmOpen] = useState(false);

  const { data: businessDayPayload } = useQuery({
    queryKey: ["business-day", branchId],
    queryFn: async () => {
      const { data } = await api.get("/business-day/current", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId),
    staleTime: 15_000,
  });
  const businessDay = businessDayPayload?.business_day;

  useEffect(() => {
    if (businessDay?.status === "open" && businessDay?.business_date) {
      setSingleDate(businessDay.business_date);
    }
  }, [businessDay?.status, businessDay?.business_date]);

  const { data: countersData } = useQuery({
    queryKey: ["counters", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/counters", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const counters = countersData?.counters ?? [];

  useEffect(() => {
    if ((activeTab === "z-report" || activeTab === "x-report") && counterId === "all" && counters.length) {
      setCounterId(String(counters[0].id));
    }
  }, [counters, counterId, activeTab]);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: [
      "cash-report",
      activeTab,
      branchId,
      allBranches,
      fromDate,
      toDate,
      singleDate,
      counterId,
      orderType,
      paymentMethod,
    ],
    queryFn: async () => {
      if (activeTab === "collection") {
        const params = { ...reportParams, from_date: fromDate, to_date: toDate };
        if (counterId && counterId !== "all") params.counter_id = counterId;
        if (orderType !== "all") params.order_type = orderType;
        if (paymentMethod !== "all") params.payment_method = paymentMethod;
        const { data: res } = await api.get(ENDPOINTS.collection, { params });
        return res;
      }
      if (activeTab === "x-report") {
        if (!counterId || counterId === "all") return null;
        const params = { ...reportParams, counter_id: counterId };
        if (orderType !== "all") params.order_type = orderType;
        if (paymentMethod !== "all") params.payment_method = paymentMethod;
        const { data: res } = await api.get(ENDPOINTS["x-report"], { params });
        return res;
      }
      if (activeTab === "z-report") {
        if (!counterId || counterId === "all") return null;
        const params = { ...reportParams, date: singleDate, counter_id: counterId };
        if (orderType !== "all") params.order_type = orderType;
        if (paymentMethod !== "all") params.payment_method = paymentMethod;
        const { data: res } = await api.get(ENDPOINTS["z-report"], { params });
        return res;
      }
      const params = { ...reportParams, date: singleDate };
      if (counterId && counterId !== "all") params.counter_id = counterId;
      if (orderType !== "all") params.order_type = orderType;
      if (paymentMethod !== "all") params.payment_method = paymentMethod;
      const { data: res } = await api.get(ENDPOINTS["day-book"], { params });
      return res;
    },
    enabled: Boolean(
      canQuery &&
        (isRange ? fromDate && toDate : isLiveSessionReport ? counterId && counterId !== "all" : singleDate) &&
        (activeTab === "collection" ||
          activeTab === "day-book" ||
          (branchId && counterId && counterId !== "all")),
    ),
  });

  useEffect(() => {
    if (report?.denomination?.lines) {
      setDenomLines(report.denomination.lines.map((l) => ({ ...l })));
    }
  }, [report?.denomination?.lines]);

  const saveDenomMutation = useMutation({
    mutationFn: async () => {
      const dateForDenom =
        activeTab === "z-report" && businessDay?.status === "open" && businessDay?.business_date
          ? businessDay.business_date
          : singleDate;
      const { data: res } = await api.post("/denomination-counts", {
        ...reportParams,
        counter_id: Number(counterId),
        date: dateForDenom,
        lines: denomLines.map((l) => ({
          denomination_id: l.denomination_id,
          qty: Number(l.qty) || 0,
        })),
      });
      return res;
    },
    onSuccess: () => {
      toast.success("Denomination count saved.");
      queryClient.invalidateQueries({ queryKey: ["cash-report"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save denomination count.");
    },
  });

  const closingBusinessDate =
    businessDay?.status === "open" && businessDay?.business_date
      ? businessDay.business_date
      : singleDate;
  const nextOpenDate = (() => {
    if (!closingBusinessDate) return "";
    const d = new Date(`${closingBusinessDate}T12:00:00`);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const dateMismatch =
    Boolean(closingBusinessDate) &&
    Boolean(businessDay?.system_date) &&
    closingBusinessDate !== businessDay.system_date;

  const requestDayEnd = () => {
    if (!counterId || counterId === "all") {
      toast.error("Select a counter first");
      return;
    }
    if (businessDay?.status !== "open" || !businessDay?.business_date) {
      toast.error("No open business day — use Day Open on POS first");
      return;
    }
    setDayEndConfirmOpen(true);
  };

  const declaredTotal = useMemo(
    () =>
      denomLines.reduce(
        (sum, l) => sum + (Number(l.qty) || 0) * (Number(l.value) || 0),
        0,
      ),
    [denomLines],
  );

  const executeDayEnd = async ({ print = false, format = "thermal" } = {}) => {
    const dayToClose = closingBusinessDate;
    const printFormat = format === "a4" ? "a4" : "thermal";
    if (print && printFormat === "thermal") {
      prepareBillPrintWindow();
    }

    setDayEnding(true);
    try {
      await api.post("/denomination-counts", {
        branch_id: branchId,
        counter_id: Number(counterId),
        date: dayToClose,
        lines: denomLines.map((l) => ({
          denomination_id: l.denomination_id,
          qty: Number(l.qty) || 0,
        })),
      });

      const { data: dayEndRes } = await api.post("/day-end", {
        branch_id: branchId,
        counter_id: Number(counterId),
        counter_session_id: report?.open_session?.id || undefined,
        business_date: dayToClose,
        system_cash: report?.cash_summary?.expected_cash ?? 0,
        denomination_counts: denomLines.map((l) => ({
          denomination_id: l.denomination_id,
          value: Number(l.value) || 0,
          qty: Number(l.qty) || 0,
        })),
        notes: "Z-Report day end",
      });

      if (report?.open_session?.id) {
        await api.post(`/counter-sessions/${report.open_session.id}/close`, {
          branch_id: branchId,
          closing_cash_counted: declaredTotal,
          close_notes: "Closed from Z-Report day end",
        });
      }

      const { data: bizEnd } = await api.post("/business-day/end", { branch_id: branchId });

      const expectedCash = report?.cash_summary?.expected_cash ?? 0;
      const liveVariance = Math.round((declaredTotal - expectedCash) * 100) / 100;
      const printPayload = report
        ? {
            ...report,
            date: dayToClose,
            denomination: {
              ...(report.denomination || {}),
              lines: denomLines,
              declared_total: declaredTotal,
              expected_cash: expectedCash,
              variance: liveVariance,
              saved: true,
            },
            day_end: {
              counted_cash: dayEndRes?.day_end_session?.counted_cash ?? declaredTotal,
              system_cash: dayEndRes?.day_end_session?.system_cash ?? expectedCash,
              difference: dayEndRes?.day_end_session?.difference ?? liveVariance,
              closed_at: dayEndRes?.day_end_session?.closed_at || new Date().toISOString(),
              closed_date: bizEnd?.closed_date || dayToClose,
              suggested_open_date: bizEnd?.suggested_open_date || nextOpenDate,
              voucher_no: dayEndRes?.day_end_session?.id,
            },
          }
        : null;

      toast.success(
        bizEnd?.message || `Day ${dayToClose} closed. Open the next day on POS before selling.`,
      );
      if (print && printPayload) {
        await printCashReport(printPayload, "z-report", {
          branchId,
          allBranches,
          cashier: cashierName,
          format: printFormat,
        });
      }
      setDayEndConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["business-day", branchId] });
      await queryClient.invalidateQueries({ queryKey: ["cash-report"] });
      await queryClient.invalidateQueries({ queryKey: ["counter-shift"] });
    } catch (err) {
      toast.error(err.response?.data?.message || "Day end failed");
    } finally {
      setDayEnding(false);
    }
  };

  const title = TABS.find((t) => t.id === activeTab)?.label ?? "Cash Reports";

  const updateDenomQty = (denominationId, qty) => {
    setDenomLines((prev) =>
      prev.map((l) =>
        l.denomination_id === denominationId
          ? {
              ...l,
              qty: Math.max(0, Number(qty) || 0),
              amount: Math.round((Math.max(0, Number(qty) || 0) * l.value) * 100) / 100,
            }
          : l,
      ),
    );
  };

  return (
    <div className="min-w-0">
      <PageHeader
        title={title}
        subtitle={
          activeTab === "collection"
            ? "Payment collections by method, counter and date"
            : activeTab === "x-report"
              ? "Mid-shift snapshot for the open counter shift (does not close the shift)"
            : activeTab === "z-report"
              ? businessDay?.status === "open"
                ? `Business day ${businessDay.business_date} — close with Day End after cash count`
                : "End-of-day counter summary with cash reconciliation (open a business day on POS first)"
              : "Chronological sales with denomination cash count"
        }
        actions={
          <ReportExportButtons
            disabled={!report}
            title={title}
            filename={`cash-${activeTab}`}
            period={formatReportFilterLine({
              fromDate: isRange ? fromDate : undefined,
              toDate: isRange ? toDate : undefined,
              date: !isRange && !isLiveSessionReport ? singleDate : undefined,
              orderType,
              paymentMethod,
              extra: isLiveSessionReport ? ["Open shift"] : [],
            })}
            printTestId="cash-rpt-print"
            onPrint={() => printCashReport(report, activeTab, { branchId, allBranches })}
          />
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2.5 text-[12.5px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                activeTab === tab.id
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-slate-600 hover:text-slate-900",
              )}
              data-testid={`cash-tab-${tab.id}`}
            >
              <Icon size={14} /> {tab.label}
            </button>
          );
        })}
      </div>

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Filters</SectionTitle>
        <div className="flex flex-wrap items-end gap-4">
          {isRange ? (
            <>
              <div>
                <Label className="text-[12px] text-slate-600 mb-1 block">From</Label>
                <Input
                  type="date"
                  className="h-9 w-[160px]"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-[12px] text-slate-600 mb-1 block">To</Label>
                <Input
                  type="date"
                  className="h-9 w-[160px]"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </>
          ) : !isLiveSessionReport ? (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Date</Label>
              <Input
                type="date"
                className="h-9 w-[160px]"
                value={singleDate}
                onChange={(e) => setSingleDate(e.target.value)}
              />
            </div>
          ) : null}
          {needsCounter && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">
                Counter {activeTab === "z-report" || activeTab === "x-report" ? "" : "(optional)"}
              </Label>
              <Select value={counterId} onValueChange={setCounterId}>
                <SelectTrigger className="h-9 w-[200px]">
                  <SelectValue placeholder="Select counter" />
                </SelectTrigger>
                <SelectContent>
                  {activeTab === "day-book" && <SelectItem value="all">All counters</SelectItem>}
                  {counters.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.device_code || c.counter_no} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {activeTab === "collection" && (
            <div>
              <Label className="text-[12px] text-slate-600 mb-1 block">Counter (optional)</Label>
              <Select value={counterId || "all"} onValueChange={setCounterId}>
                <SelectTrigger className="h-9 w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All counters</SelectItem>
                  {counters.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.device_code || c.counter_no} — {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <OrderTypeFilter value={orderType} onChange={setOrderType} />
          <PaymentMethodFilter value={paymentMethod} onChange={setPaymentMethod} />
          {(isLoading || isFetching) && (
            <Pill className="mb-1">
              <Loader2 size={12} className="animate-spin mr-1" /> Loading…
            </Pill>
          )}
        </div>
      </Card>

      {report && activeTab === "collection" && <CollectionView report={report} />}
      {report && activeTab === "x-report" && <XReportView report={report} />}
      {report && activeTab === "z-report" && (
        <ZReportView
          report={report}
          denomLines={denomLines}
          declaredTotal={declaredTotal}
          onDenomChange={updateDenomQty}
          onSaveDenom={() => saveDenomMutation.mutate()}
          savingDenom={saveDenomMutation.isPending}
          canEditDenom={counterId && counterId !== "all"}
          businessDay={businessDay}
          onRequestDayEnd={requestDayEnd}
          dayEnding={dayEnding}
        />
      )}
      {report && activeTab === "day-book" && (
        <DayBookView
          report={report}
          denomLines={denomLines}
          declaredTotal={declaredTotal}
          onDenomChange={updateDenomQty}
          onSaveDenom={() => saveDenomMutation.mutate()}
          savingDenom={saveDenomMutation.isPending}
          canEditDenom={counterId && counterId !== "all"}
        />
      )}

      <DayEndConfirmDialog
        open={dayEndConfirmOpen}
        onOpenChange={setDayEndConfirmOpen}
        onConfirm={executeDayEnd}
        loading={dayEnding}
        dayToClose={closingBusinessDate}
        nextOpenDate={nextOpenDate}
        counterName={
          counters.find((c) => String(c.id) === String(counterId))?.name
          || report?.counter?.name
        }
        systemDate={businessDay?.system_date}
        dateMismatch={dateMismatch}
        countedTotal={declaredTotal}
        settlement={{
          cash: report?.cash_summary?.cash_received,
          card: (report?.payments || []).find((p) => p.payment_method === "card")?.amount,
          credit: (report?.payments || []).find((p) => p.payment_method === "credit")?.amount,
          cashOut: report?.cash_summary?.cash_refunds,
          expectedCash: report?.cash_summary?.expected_cash,
          variance: Math.round((declaredTotal - (report?.cash_summary?.expected_cash || 0)) * 100) / 100,
        }}
      />
    </div>
  );
}

function CollectionView({ report }) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Bills", value: num(report.summary?.bill_count) },
          { label: "Round off", value: aed(report.summary?.round_off) },
          { label: "Total collected", value: aed(report.summary?.total_collected), emphasize: true },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ReportTableCard title="By payment method" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Method</th>
                <th className="text-right">Bills</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(report.summary?.by_method || []).map((r) => (
                <tr key={r.payment_method}>
                  <td>{r.payment_method_label}</td>
                  <td className="text-right">{r.bill_count}</td>
                  <td className="text-right font-mono font-medium">{aed(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>

        <ReportTableCard title="By counter" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Counter</th>
                <th className="text-right">Bills</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(report.by_counter || []).map((r) => (
                <tr key={r.counter_id}>
                  <td>{r.counter_name}</td>
                  <td className="text-right">{r.bill_count}</td>
                  <td className="text-right font-mono font-medium">{aed(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      </div>

      <ReportTableCard title="Detail">
        <table>
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Counter</th>
              <th className="text-left">Method</th>
              <th className="text-right">Bills</th>
              <th className="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(report.rows || []).map((r, i) => (
              <tr key={`${r.date}-${r.counter_id}-${r.payment_method}-${i}`}>
                <td>{r.date}</td>
                <td>{r.counter_name}</td>
                <td>{r.payment_method_label}</td>
                <td className="text-right">{r.bill_count}</td>
                <td className="text-right font-mono">{aed(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportTableCard>
    </>
  );
}

function XReportView({ report }) {
  const cs = report.cash_summary || {};
  const session = report.session || {};

  return (
    <>
      <Card className="p-4 mb-4 bg-emerald-50 border-emerald-200">
        <p className="text-[13px] text-emerald-900 font-medium">
          Live shift — opened {session.opened_at ? new Date(session.opened_at).toLocaleString() : "—"}
          {session.opened_by_name ? ` by ${session.opened_by_name}` : ""}
        </p>
        <p className="text-[12px] text-emerald-800 mt-1">
          This report does not close the shift. Use Z-Report or Close shift at end of day.
        </p>
      </Card>

      <ReportKpiStrip
        items={[
          { label: "Bills", value: num(report.sales?.bill_count) },
          { label: "Round off", value: aed(report.sales?.round_off) },
          { label: "Net sales", value: aed(report.sales?.net_sales), emphasize: true },
          { label: "Opening float", value: aed(cs.opening_float) },
          { label: "Cash in", value: aed(cs.cash_in) },
          { label: "Cash out", value: aed(cs.cash_out) },
          { label: "Expected cash", value: aed(cs.expected_cash), emphasize: true },
        ]}
      />

      <ZReportView
        report={{
          ...report,
          cash_summary: {
            cash_received: cs.cash_received,
            cash_refunds: 0,
            change_given: cs.change_given,
            expected_cash: cs.expected_cash,
          },
          denomination: { lines: [] },
        }}
        hideSalesKpis
      />

      {(report.cash_movements || []).length > 0 && (
        <ReportTableCard title="Cash in / out this shift">
          <table>
            <thead>
              <tr>
                <th className="text-left">Time</th>
                <th className="text-left">Type</th>
                <th className="text-left">Reason</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {report.cash_movements.map((m) => (
                <tr key={m.id}>
                  <td className="text-slate-500">
                    {m.created_at ? new Date(m.created_at).toLocaleTimeString() : "—"}
                  </td>
                  <td className="capitalize">{m.direction}</td>
                  <td>{m.reason_label}</td>
                  <td className="text-right font-mono">{aed(m.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}
    </>
  );
}

function ZReportView({
  report,
  hideSalesKpis = false,
  denomLines = [],
  declaredTotal = 0,
  onDenomChange,
  onSaveDenom,
  savingDenom = false,
  canEditDenom = false,
  businessDay,
  onRequestDayEnd,
  dayEnding = false,
}) {
  const cs = report.cash_summary || {};
  const denom = report.denomination || {};
  const lines = denomLines.length ? denomLines : denom.lines || [];

  return (
    <>
      {!hideSalesKpis && (
        <ReportKpiStrip
          items={[
            { label: "Bills", value: num(report.sales?.bill_count) },
            { label: "Gross", value: aed(report.sales?.gross_sales) },
            { label: "Discount", value: aed(report.sales?.discount) },
            { label: "VAT", value: aed(report.sales?.vat_collected) },
            { label: "Round off", value: aed(report.sales?.round_off) },
            { label: "Net sales", value: aed(report.sales?.net_sales), emphasize: true },
            { label: "Returns", value: aed(report.sales?.returns) },
          ]}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ReportTableCard title="Payment breakdown" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Method</th>
                <th className="text-right">Bills</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Change</th>
              </tr>
            </thead>
            <tbody>
              {(report.payments || []).map((p) => (
                <tr key={p.payment_method}>
                  <td>{p.payment_method_label}</td>
                  <td className="text-right">{p.bill_count}</td>
                  <td className="text-right font-mono">{aed(p.amount)}</td>
                  <td className="text-right font-mono">{aed(p.change_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>

        <Card className="p-4 mb-0">
          <SectionTitle className="!mb-3">Cash reconciliation</SectionTitle>
          <dl className="space-y-2 text-[12.5px]">
            <div className="flex justify-between"><dt className="text-slate-600">Cash received</dt><dd className="font-mono">{aed(cs.cash_received)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">Cash refunds</dt><dd className="font-mono">{aed(cs.cash_refunds)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-600">Change given</dt><dd className="font-mono">{aed(cs.change_given)}</dd></div>
            <div className="flex justify-between border-t pt-2 font-medium">
              <dt>Expected cash</dt><dd className="font-mono">{aed(cs.expected_cash)}</dd>
            </div>
            <div className="flex justify-between"><dt className="text-slate-600">Declared (denomination)</dt><dd className="font-mono">{aed(declaredTotal || denom.declared_total)}</dd></div>
            {(declaredTotal || denom.declared_total) != null && (
              <div className={cn("flex justify-between font-medium", (declaredTotal - (cs.expected_cash || 0)) !== 0 && "text-amber-700")}>
                <dt>Variance</dt>
                <dd className="font-mono">{aed((declaredTotal || denom.declared_total || 0) - (cs.expected_cash || 0))}</dd>
              </div>
            )}
          </dl>
          {typeof onRequestDayEnd === "function" && (
            <div className="mt-4 pt-3 border-t space-y-2">
              <p className="text-[11px] text-slate-500">
                {businessDay?.status === "open"
                  ? `Close business day ${businessDay.business_date} after counting cash.`
                  : "Open a business day on POS before Day End."}
              </p>
              <Button
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={dayEnding || businessDay?.status !== "open" || !canEditDenom}
                onClick={onRequestDayEnd}
                data-testid="z-day-end"
              >
                {dayEnding ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Day End / Close business day
              </Button>
            </div>
          )}
        </Card>
      </div>

      {canEditDenom && lines.length > 0 && typeof onDenomChange === "function" && (
        <Card className="p-4 mb-3">
          <div className="flex items-center justify-between mb-3">
            <SectionTitle className="!mb-0">Denomination count</SectionTitle>
            <Button size="sm" className="h-8" onClick={onSaveDenom} disabled={savingDenom} data-testid="save-denomination-z">
              {savingDenom ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1" /> Save</>}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {lines.map((l) => (
              <div key={l.denomination_id} className="flex items-center gap-2">
                <span className="w-14 text-[12px] font-mono text-slate-600">{l.label}</span>
                <Input
                  type="number"
                  min={0}
                  className="h-8 w-20 text-right"
                  value={l.qty}
                  onChange={(e) => onDenomChange(l.denomination_id, e.target.value)}
                />
                <span className="flex-1 text-right text-[12px] font-mono text-slate-700">
                  {aed((Number(l.qty) || 0) * l.value)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-4 pt-3 border-t text-[13px] font-medium">
            <span>Declared total</span>
            <span className="font-mono">{aed(declaredTotal)}</span>
          </div>
        </Card>
      )}

      {!canEditDenom && denom.lines?.length > 0 && (
        <ReportTableCard title="Denomination count">
          <table>
            <thead>
              <tr>
                <th className="text-left">Value</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {denom.lines.filter((l) => l.qty > 0).map((l) => (
                <tr key={l.denomination_id}>
                  <td className="font-mono">{l.label}</td>
                  <td className="text-right">{l.qty}</td>
                  <td className="text-right font-mono">{aed(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>
      )}
    </>
  );
}

function DayBookView({
  report,
  denomLines,
  declaredTotal,
  onDenomChange,
  onSaveDenom,
  savingDenom,
  canEditDenom,
}) {
  return (
    <>
      <ReportKpiStrip
        items={[
          { label: "Bills", value: num(report.summary?.bill_count) },
          { label: "VAT", value: aed(report.summary?.vat_collected) },
          { label: "Round off", value: aed(report.summary?.round_off) },
          { label: "Net sales", value: aed(report.summary?.net_sales), emphasize: true },
        ]}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-3">
        <ReportTableCard title="Sales entries" className="mb-0">
          <table>
            <thead>
              <tr>
                <th className="text-left">Bill</th>
                <th className="text-left">Time</th>
                <th className="text-left">Type</th>
                <th className="text-left">Counter</th>
                <th className="text-left">Customer</th>
                <th className="text-left">TRN</th>
                <th className="text-right">Round off</th>
                <th className="text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {(report.entries || []).map((e) => (
                <tr key={e.id}>
                  <td className="font-mono text-[12px]">{e.bill_number}</td>
                  <td>{e.paid_time}</td>
                  <td>{e.order_type_label}</td>
                  <td>{e.counter_name}</td>
                  <td>{e.customer_name || "—"}</td>
                  <td className="font-mono text-[12px]">{e.customer_tax_number || "—"}</td>
                  <td className="text-right font-mono">{aed(e.round_off)}</td>
                  <td className="text-right font-mono font-medium">{aed(e.net_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportTableCard>

        {canEditDenom && denomLines.length > 0 && (
          <Card className="p-4 h-fit">
            <div className="flex items-center justify-between mb-3">
              <SectionTitle className="!mb-0">Denomination</SectionTitle>
              <Button
                size="sm"
                className="h-8"
                onClick={onSaveDenom}
                disabled={savingDenom}
                data-testid="save-denomination"
              >
                {savingDenom ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <><Save size={14} className="mr-1" /> Save</>
                )}
              </Button>
            </div>
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {denomLines.map((l) => (
                <div key={l.denomination_id} className="flex items-center gap-2">
                  <span className="w-14 text-[12px] font-mono text-slate-600">{l.label}</span>
                  <Input
                    type="number"
                    min={0}
                    className="h-8 w-20 text-right"
                    value={l.qty}
                    onChange={(e) => onDenomChange(l.denomination_id, e.target.value)}
                  />
                  <span className="flex-1 text-right text-[12px] font-mono text-slate-700">
                    {aed((Number(l.qty) || 0) * l.value)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-4 pt-3 border-t text-[13px] font-medium">
              <span>Declared total</span>
              <span className="font-mono">{aed(declaredTotal)}</span>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
